import { gql } from '@apollo/client';
import { Card, CardDescription, CardHeader, CardTitle } from '@apollo/orbit';
import classnames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { Link, useLocation } from 'react-router-dom';

import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { proposalChangesRouteConfig, proposalRouteConfig } from '../routes';

const DiffSummarySummaryFragment = gql`
  fragment DiffSummarySummaryFragment on FlatDiffTypeSummary {
    add
    remove
    change
    typeCount
  }
`;

export const DiffSummaryDiffFragment = gql`
  fragment DiffSummaryDiffFragment on FlatDiff {
    id
    summary {
      object {
        ...DiffSummarySummaryFragment
      }
      directive {
        ...DiffSummarySummaryFragment
      }
      enum {
        ...DiffSummarySummaryFragment
      }
      input {
        ...DiffSummarySummaryFragment
      }
      interface {
        ...DiffSummarySummaryFragment
      }
      scalar {
        ...DiffSummarySummaryFragment
      }
      schema {
        ...DiffSummarySummaryFragment
      }
      union {
        ...DiffSummarySummaryFragment
      }
    }
  }
  ${DiffSummarySummaryFragment}
`;
export function DiffSummary({
  diff,
  shouldLinkToChangesPage,
  subgraphName,
}: {
  diff: GraphQLTypes.DiffSummaryDiffFragment;
  shouldLinkToChangesPage?: boolean;
  subgraphName?: string;
}) {
  const location = useLocation();
  const containerSize = useResizeDetector({ handleHeight: false });
  const summaries: Array<[string, GraphQLTypes.DiffSummarySummaryFragment]> =
    Object.entries(diff.summary).filter(
      (
        entry,
      ): entry is typeof entry & { 1: Exclude<(typeof entry)[1], string> } => {
        const [, summary] = entry;
        if (typeof summary === 'string') return false;
        return Object.values(summary).some(
          (value) => value !== 'FlatDiffTypeSummary' && value > 0,
        );
      },
    );
  const widthPerCard = (containerSize.width ?? 0) / summaries.length;
  const columnCount = Math.min(
    Math.floor((containerSize.width ?? 0) / 200),
    summaries.length,
  );
  const isSmallWidthLayout = widthPerCard < 120 || widthPerCard > 200;

  return (
    <div
      ref={containerSize.ref}
      className={classnames(
        'gap-3',
        {
          'grid-cols-[minmax(0,400px)]': columnCount <= 1,
          'grid-cols-[repeat(2,minmax(0,320px))]': columnCount === 2,
          'grid-cols-[repeat(3,minmax(0,320px))]': columnCount === 3,
          'grid-cols-[repeat(4,minmax(0,320px))]': columnCount === 4,
          'grid-cols-[repeat(5,minmax(0,320px))]': columnCount === 5,
          'grid-cols-[repeat(6,minmax(0,320px))]': columnCount === 6,
          'grid-cols-[repeat(7,minmax(0,320px))]': columnCount === 7,
          'grid-cols-[repeat(8,minmax(0,320px))]': columnCount >= 8,
        },
        isSmallWidthLayout ? 'grid' : 'flex',
      )}
    >
      {summaries.length === 0 ? (
        <i className="text-secondary">
          There are no semantic changes associated with this revision.
        </i>
      ) : (
        summaries.map(([type, summary]) => {
          const linkedGroupName =
            type === 'schema' ? '#Schema' : `#${_.capitalize(type)}s`;
          return (
            <WrapMaybe
              key={type}
              on={!!shouldLinkToChangesPage}
              element={
                <Link
                  to={proposalChangesRouteConfig.locationFrom({
                    location,
                    patch: {
                      linkedGroupName,
                      ...(subgraphName ? { selectedSchema: subgraphName } : {}),
                      linkedCoordinateName: RouteConfig.DELETE,
                      linkedCommentId: RouteConfig.DELETE,
                    },
                    fromRouteConfig: proposalRouteConfig,
                  })}
                />
              }
            >
              <Card
                className={classnames('flex-1', {
                  'min-w-1/3': isSmallWidthLayout,
                })}
              >
                <CardHeader
                  className={classnames(
                    'h-full justify-between',
                    isSmallWidthLayout ? 'flex-row' : 'flex-col',
                  )}
                >
                  <div>
                    <CardTitle>{summary.typeCount}</CardTitle>
                    <CardDescription>
                      {_.upperFirst(
                        format.pluralize(
                          summary.typeCount,
                          type === 'schema' ? 'Schema Definition' : type,
                        ),
                      )}
                    </CardDescription>
                  </div>
                  <div className={classnames({ 'pt-4': !isSmallWidthLayout })}>
                    <SummaryItem
                      action="added"
                      count={summary.add}
                      className="pr-2"
                    />
                    <SummaryItem
                      action="removed"
                      count={summary.remove}
                      className="pr-2"
                    />
                    <SummaryItem
                      action="changed"
                      count={summary.change}
                      className="pr-2"
                    />
                  </div>
                </CardHeader>
              </Card>
            </WrapMaybe>
          );
        })
      )}
    </div>
  );
}

export function SummaryItem({
  count,
  action,
  className,
}: {
  count: number | null;
  action: 'added' | 'removed' | 'changed';
  className?: string;
}) {
  return (
    <span
      className={classnames(className, 'font-semibold', {
        'text-success': action === 'added',
        'text-icon-change': action === 'changed',
        'text-error': action === 'removed',
      })}
    >
      {action === 'added' ? (
        '+'
      ) : action === 'removed' ? (
        '-'
      ) : action === 'changed' ? (
        <span className="mr-0.5 inline-block size-1.5 rounded-full bg-icon-change align-middle" />
      ) : (
        assertUnreachable(action)
      )}
      {count}
    </span>
  );
}
